import React, {FC} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid} from "@mui/material";
import {Consumer} from "./hooks/useConsumer";
import {FormInputText} from "../../wholesale/form/FormInputText";
import {FormInputSelect} from "../../wholesale/form/FormInputSelect";
import {FormInputCurrency} from "../../wholesale/form/FormInputCurrency";
import {SavingUI} from "../../../../SavingProvider";
import {useConsumerUpdateMutation} from "../../../../Queries";

const Countries = [
   {id: "AT", name: "Austria"},
   {id: "BE", name: "Belgium"},
   {id: "BG", name: "Bulgaria"},
   {id: "HR", name: "Croatia"},
   {id: "CY", name: "Cyprus"},
   {id: "CZ", name: "Czech Republic"},
   {id: "DK", name: "Denmark"},
   {id: "EE", name: "Estonia"},
   {id: "FI", name: "Finland"},
   {id: "FR", name: "France"},
   {id: "DE", name: "Germany"},
   {id: "GR", name: "Greece"},
   {id: "HU", name: "Hungary"},
   {id: "IE", name: "Ireland"},
   {id: "IT", name: "Italy"},
   {id: "LV", name: "Latvia"},
   {id: "LT", name: "Lithuania"},
   {id: "LU", name: "Luxembourg"},
   {id: "MT", name: "Malta"},
   {id: "NL", name: "Netherlands"},
   {id: "NO", name: "Norway"},
   {id: "PL", name: "Poland"},
   {id: "PT", name: "Portugal"},
   {id: "RO", name: "Romania"},
   {id: "SK", name: "Slovakia"},
   {id: "SI", name: "Slovenia"},
   {id: "ES", name: "Spain"},
   {id: "SE", name: "Sweden"},
   {id: "CH", name: "Switzerland"},
   {id: "GB", name: "United Kingdom"},
   {id: "US", name: "United States"}
];

interface ConsumerEditFormProps {
   consumer: Consumer;
   open: boolean;
   onClose: () => void;
   onSave: () => void;
}

interface ConsumerFormData {
   name: string;
   add1: string;
   add2: string;
   countryCode: string;
   currency: string;
   zipCode: string;
   phone: string;
   mail: string;
}

export const ConsumerEditForm: FC<ConsumerEditFormProps> = ({consumer, open, onClose, onSave}) => {
   const [updateConsumer] = useConsumerUpdateMutation();

   const methods = useForm<ConsumerFormData>({
      defaultValues: {
         name: `${consumer.firstName} ${consumer.lastName}`,
         add1: consumer.add1 || "",
         add2: consumer.add2 || "",
         countryCode: consumer.countryCode || "",
         currency: "EUR", // Default currency since it's not in the consumer data
         zipCode: consumer.zipCode || "",
         phone: consumer.phone || "",
         mail: consumer.mail || ""
      }
   });

   const handleSubmit = async (data: ConsumerFormData) => {
      await SavingUI.process(async () => {
         const [firstName, ...lastNameParts] = data.name.split(" ");
         const lastName = lastNameParts.join(" ");

         await updateConsumer({
            variables: {
               input: {
                  _id: consumer._id,
                  shopId: consumer.shopId,
                  firstName: firstName || "",
                  lastName: lastName || "",
                  add1: data.add1,
                  add2: data.add2,
                  countryCode: data.countryCode,
                  zipCode: data.zipCode,
                  phone: data.phone,
                  mail: data.mail
               }
            }
         });
         onSave();
         onClose();
      }, "Updating consumer");
   };

   return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
         <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(handleSubmit)}>
               <DialogTitle>Edit Consumer</DialogTitle>
               <DialogContent>
                  <Grid container spacing={2} sx={{mt: 1}}>
                     <Grid item xs={12}>
                        <FormInputText name="name" label="Name" required/>
                     </Grid>
                     <Grid item xs={12}>
                        <FormInputText name="add1" label="Address Line 1" required/>
                     </Grid>
                     <Grid item xs={12}>
                        <FormInputText name="add2" label="Address Line 2"/>
                     </Grid>
                     <Grid item xs={12}>
                        <FormInputSelect
                           name="countryCode"
                           label="Country"
                           options={Countries}
                           required
                        />
                     </Grid>
                     <Grid item xs={12}>
                        <FormInputCurrency
                           name="currency"
                           label="Currency"
                        />
                     </Grid>
                     <Grid item xs={6}>
                        <FormInputText name="zipCode" label="Zip Code" required/>
                     </Grid>
                     <Grid item xs={6}>
                        <FormInputText name="phone" label="Phone" required/>
                     </Grid>
                     <Grid item xs={12}>
                        <FormInputText name="mail" label="Email" required/>
                     </Grid>
                  </Grid>
               </DialogContent>
               <DialogActions>
                  <Button onClick={onClose}>Cancel</Button>
                  <Button type="submit" variant="contained">Save</Button>
               </DialogActions>
            </form>
         </FormProvider>
      </Dialog>
   );
};